import { logo } from 'monica-alexandria';
import { config } from '../../../config';

const read_env_value = (value) => {
    return `${value || ''}`.trim().replace(/^['"]|['"]$/g, '');
};

const trim_trailing_slash = (value) => {
    return read_env_value(value).replace(/\/+$/, '');
};

const compose_url = (protocol, host, port) => {
    const normalizedProtocol = read_env_value(protocol);
    const normalizedHost = read_env_value(host);
    const normalizedPort = read_env_value(port);

    if (!normalizedProtocol.length || !normalizedHost.length) {
        return '';
    }

    return trim_trailing_slash(`${normalizedProtocol}${normalizedHost}${normalizedPort}`);
};

const route_matches = (pathname, suffix) => {
    return new RegExp(`^/[^/]+/${suffix}/?$`).test(pathname || '');
};

const server_domain_suffix = (serverValue) => {
    const normalizedServer = read_env_value(serverValue).toUpperCase();

    if (normalizedServer === 'STAGING') {
        return 'club';
    }

    return 'eu';
};

const fallback_app_urls = (serverValue) => {
    const normalizedServer = read_env_value(serverValue).toUpperCase();
    const suffix = server_domain_suffix(normalizedServer);

    return {
        questad: `https://questad.doitforme.${suffix}`,
        mcp: '',
        planner: `https://planner.doitforme.${suffix}`,
        loop: `https://noblabla.doitforme.${suffix}`,
        adsnap: `https://adsnap.doitforme.${suffix}`,
        booker: `https://market.doitforme.${suffix}`,
        leads: `https://leads.doitforme.${suffix}`,
        hr: `https://hr.doitforme.${suffix}`,
        tasks: `https://tasks.doitforme.${suffix}`,
        career: ''
    };
};

const read_frontend_app_url = (envPrefix, fallbackUrl) => {
    const directUrl = read_env_value(process.env[`REACT_APP_${envPrefix}_FRONTEND_URL`]);
    const compositeUrl = compose_url(
        process.env[`REACT_APP_${envPrefix}_FRONTEND_PROTOCOL`],
        process.env[`REACT_APP_${envPrefix}_FRONTEND_HOST`],
        process.env[`REACT_APP_${envPrefix}_FRONTEND_PORT`]
    );

    return trim_trailing_slash(directUrl || compositeUrl || fallbackUrl);
};

const ecosystem_app_urls = () => {
    const server = read_env_value(config?.server || process.env.REACT_APP_SERVER || 'PRODUCTION').toUpperCase();
    const fallbackUrls = fallback_app_urls(server);

    return {
        questad: read_frontend_app_url('DAILYPROFIT', fallbackUrls.questad),
        mcp: read_frontend_app_url('MCP', fallbackUrls.mcp),
        planner: read_frontend_app_url('TIMETURNER', fallbackUrls.planner),
        loop: read_frontend_app_url('VERDICT', fallbackUrls.loop),
        adsnap: read_frontend_app_url('LOIS', fallbackUrls.adsnap),
        booker: read_frontend_app_url('PEPPERPOTS', fallbackUrls.booker),
        leads: read_frontend_app_url('NIFFLER', fallbackUrls.leads),
        hr: read_frontend_app_url('DOLORES', fallbackUrls.hr),
        tasks: read_frontend_app_url('CEREBRO', fallbackUrls.tasks),
        career: read_frontend_app_url('SORTINGHAT', fallbackUrls.career)
    };
};

export const sidebar_portal_base_urls = () => {
    const appUrls = ecosystem_app_urls();

    return {
        dailyprofit: appUrls.questad,
        mcp: appUrls.mcp,
        timeturner: appUrls.planner,
        verdict: appUrls.loop,
        niffler: appUrls.leads,
        sortinghat: appUrls.career,
        lois: appUrls.adsnap,
        dolores: appUrls.hr,
        pepperpots: appUrls.booker,
        godfather: '',
        cerebro: appUrls.tasks
    };
};

export const sidebar_portal_item_state = () => {
    const baseUrls = sidebar_portal_base_urls();
    const keys = Object.keys(baseUrls || {});

    return keys.reduce((accumulator, currentKey) => {
        if (currentKey === 'godfather') {
            return accumulator;
        }

        if (`${baseUrls[currentKey] || ''}`.trim().length > 0) {
            return accumulator;
        }

        return {
            ...accumulator,
            [currentKey]: {
                disabled: true,
                comingSoon: true
            }
        };
    }, {});
};

const tool_route = (baseUrl, routeLanguage, suffix) => {
    const normalizedBaseUrl = trim_trailing_slash(baseUrl);

    if (!normalizedBaseUrl.length) {
        return '';
    }

    return `${normalizedBaseUrl}/${routeLanguage}${suffix}`;
};

const with_disabled_state = (item) => {
    const hasUrl = `${item?.url || ''}`.trim().length > 0;

    if (hasUrl) {
        return {
            ...item,
            disabled: false,
            comingSoon: false
        };
    }

    return {
        ...item,
        disabled: true,
        comingSoon: true,
        url: ''
    };
};

export const sidebar_tools_apps = (t, routeLanguage) => {
    const appUrls = ecosystem_app_urls();

    return [
        with_disabled_state({
            name: t('Questad'),
            image: logo.dailyprofit.light,
            url: tool_route(appUrls.questad, routeLanguage, '/overview')
        }),
        with_disabled_state({
            name: t('Planner'),
            image: logo.timeturner?.light || logo.bond.light,
            url: tool_route(appUrls.planner, routeLanguage, '')
        }),
        with_disabled_state({
            name: t('Loop'),
            image: logo.bond.light,
            url: tool_route(appUrls.loop, routeLanguage, '')
        }),
        with_disabled_state({
            name: t('CRM'),
            image: logo.doitmentation.light,
            url: tool_route(appUrls.leads, routeLanguage, '/crm')
        }),
        with_disabled_state({
            name: t('Adsnap'),
            image: logo.lois.light,
            url: tool_route(appUrls.adsnap, routeLanguage, '')
        })
    ];
};

export const sidebar_admin_apps = (t, routeLanguage, pathname) => {
    const appUrls = ecosystem_app_urls();
    const isOffersHomePath = /^\/[^/]+\/?$/.test(pathname || '');

    return [
        {
            name: t('HR'),
            image: logo.dolores?.light || logo.bond.light,
            url: tool_route(appUrls.hr, routeLanguage, ''),
            active: false
        },
        {
            name: t('Booker'),
            image: logo.bond.light,
            url: tool_route(appUrls.booker, routeLanguage, ''),
            active: false
        },
        {
            name: t('Leads'),
            image: logo.niffler?.light || logo.doitmentation.light,
            url: tool_route(appUrls.leads, routeLanguage, '/leads'),
            active: false
        },
        {
            name: t('Tasks'),
            image: logo.bond.light,
            url: tool_route(appUrls.tasks, routeLanguage, ''),
            active: false
        },
        {
            name: t('Offers'),
            image: logo.dailyprofit.light,
            url: `/${routeLanguage}`,
            active: isOffersHomePath
        }
    ];
};

export const sidebar_controls_options = (t, routeLanguage, pathname, canAccessPaidOffers) => {
    return [
        {
            name: t('Sales'),
            icon: 'Analytics',
            active: route_matches(pathname, 'sales'),
            onClick: () => window.location.assign(`/${routeLanguage}/sales`)
        },
        {
            name: t('Personalised ads'),
            icon: 'SocialPost',
            active: route_matches(pathname, 'marketing'),
            onClick: () => window.location.assign(`/${routeLanguage}/marketing`)
        },
        {
            name: t('Website'),
            icon: 'Website',
            active: route_matches(pathname, 'website'),
            onClick: () => window.location.assign(`/${routeLanguage}/website`)
        },
        {
            name: t('One-offs'),
            icon: 'Images',
            active: route_matches(pathname, 'creative'),
            onClick: () => window.location.assign(`/${routeLanguage}/creative`)
        }
    ];
};

export const sidebar_reports_options = (t, routeLanguage, pathname, isBoD) => {
    if (!isBoD) {
        return [];
    }

    return [];
};

export const sidebar_targets_options = (t, routeLanguage, pathname) => {
    return [];
};
